import React from "react";
import { motion } from "framer-motion";

/**
 * Key Features — 8 (or N) premium white cards on cream-light bg.
 * Orange icon plate that flips to solid orange on hover.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Key Features")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { Icon, title, desc }[]
 *  - gridCols: string (default "lg:grid-cols-4")
 *  - headerWidthClass: string (default "max-w-3xl mb-14 md:mb-16")
 */
export default function KeyFeatures({
  testId,
  eyebrow = "Key Features",
  heading,
  subtitle,
  items = [],
  gridCols = "lg:grid-cols-4",
  headerWidthClass = "max-w-3xl mb-14 md:mb-16",
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-cream-light py-24 md:py-32 text-liftile-matte-black overflow-hidden"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className={headerWidthClass}
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-matte-black/70 mt-6 leading-relaxed">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 md:gap-7`}
        >
          {items.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`${testId}-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.55,
                delay: i * 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative h-full flex flex-col bg-white rounded-2xl p-7 md:p-8 shadow-[0_4px_18px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.22)] border border-transparent hover:border-liftile-orange/40 hover:-translate-y-1 transition-all duration-500"
            >
              <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-14 rounded-bl-full" />
              <div className="w-12 h-12 rounded-xl bg-liftile-orange/10 group-hover:bg-liftile-orange flex items-center justify-center transition-colors duration-500">
                <f.Icon
                  className="w-5 h-5 text-liftile-orange group-hover:text-white transition-colors duration-500"
                  strokeWidth={1.6}
                />
              </div>
              <h3 className="font-heading text-[15px] uppercase tracking-wider font-semibold mt-5 text-liftile-matte-black leading-snug">
                {f.title}
              </h3>
              <p className="font-body text-[14px] text-liftile-matte-black/70 mt-3 leading-relaxed flex-1">
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
